// Shared drag-to-resize splitter for two side-by-side grids.
//
// Each form's per-form variant only differs by the breakpoint, the min-ratio
// (custom split per form), optional minimum/maximum ratios or pixel bounds,
// and a `prefix` used to derive ids / CSS var / class names by convention:
//
//   containerId   = `${prefix}_grids_split`     (override: opts.containerId)
//   leftId        = `${prefix}_grids_left`      (override: opts.leftId; null
//                                                 means apply width to the
//                                                 container itself)
//   handleId      = `${prefix}_grids_handle`    (override: opts.handleId)
//   cssVar        = `--${prefix}-grids-left-width`
//   resizedClass  = `${prefix}-grids-left-resized` (null to skip)
//   bodyClass     = `${prefix}-splitter-dragging`
//
// If containerId is not found, falls back to the left element's parentElement
// (kept for childpartssr where the container has no id).
//
// The function is idempotent per prefix via a window-scoped init flag.

export function initGridsSplitter(opts) {
  const prefix = opts.prefix;
  const containerId = opts.containerId || `${prefix}_grids_split`;
  const leftId = opts.leftId !== undefined ? opts.leftId : `${prefix}_grids_left`;
  const handleId = opts.handleId || `${prefix}_grids_handle`;
  const cssVar = opts.cssVar || `--${prefix}-grids-left-width`;
  const resizedClass = opts.resizedClass !== undefined
    ? opts.resizedClass
    : `${prefix}-grids-left-resized`;
  const bodyClass = opts.bodyClass || `${prefix}-splitter-dragging`;
  const minBreakpointPx = opts.minBreakpointPx || 0;
  const minRatio = opts.minRatio != null ? opts.minRatio : 0.20;
  const maxRatio = opts.maxRatio != null ? opts.maxRatio : 1 - minRatio;
  const minPx = opts.minPx != null ? opts.minPx : 200;
  const maxPx = opts.maxPx;

  const initFlag = `__splitInit_${prefix}`;
  if (window[initFlag]) return;
  window[initFlag] = true;

  let dragging = false;

  function isDesktop() {
    if (!minBreakpointPx) return true;
    return window.matchMedia(`(min-width: ${minBreakpointPx}px)`).matches;
  }

  function applyWidth(clientX) {
    const left = leftId ? document.getElementById(leftId) : null;
    const handle = document.getElementById(handleId);
    let container = document.getElementById(containerId);
    if (!container && left) container = left.parentElement;
    const target = left || container;
    if (!container || !target || !handle) return;
    if (!isDesktop()) return;
    const rect = container.getBoundingClientRect();
    let newWidth = clientX - rect.left;
    const minW = Math.max(minPx, rect.width * minRatio);
    let maxW = Math.min(
      rect.width - minPx - handle.offsetWidth,
      rect.width * maxRatio,
    );
    if (maxPx != null) maxW = Math.min(maxW, maxPx);
    
    if (maxW < minW) return;
    if (newWidth < minW) newWidth = minW;
    if (newWidth > maxW) newWidth = maxW;
    target.style.setProperty(cssVar, newWidth + 'px');
    if (resizedClass) target.classList.add(resizedClass);
  }

  function startDrag(e) {
    const t = e.target;
    if (!t || t.id !== handleId) return;
    if (!isDesktop()) return;
    e.preventDefault();
    dragging = true;
    document.body.classList.add(bodyClass);
  }

  function endDrag() {
    if (!dragging) return;
    dragging = false;
    document.body.classList.remove(bodyClass);
  }

  document.addEventListener('mousedown', startDrag);
  document.addEventListener('mousemove', (e) => {
    if (dragging) applyWidth(e.clientX);
  });
  document.addEventListener('mouseup', endDrag);
  // Recover if the window loses focus or the cursor leaves the document mid-drag.
  window.addEventListener('blur', endDrag);
  document.addEventListener('mouseleave', endDrag);

  document.addEventListener('touchstart', startDrag, { passive: false });
  document.addEventListener('touchmove', (e) => {
    if (!dragging || !e.touches || !e.touches.length) return;
    // Block native page panning so the touch is consumed by the splitter.
    e.preventDefault();
    applyWidth(e.touches[0].clientX);
  }, { passive: false });
  document.addEventListener('touchend', endDrag);
  document.addEventListener('touchcancel', endDrag);
}

// Shared client-side policy for Expected Return Date columns across SSR issue flows.
export function installExpectedReturnDateOverdueRule(gridApi, hasOutstandingBalance, considersActualReturnDate) {
  if (!gridApi || typeof gridApi.getColumnDefs !== "function") return;
  const columnDefs = gridApi.getColumnDefs() || [];
  let changed = false;
  columnDefs.forEach((column) => {
    if (!column || column.field !== "expected_return_date") return;
    column.cellClassRules = {
      "mi-expected-return-date-overdue": (params) => {
        const row = params && params.data ? params.data : {};
        const expectedDate = new Date(String(params && params.value ? params.value : ""));
        if (Number.isNaN(expectedDate.getTime())) return false;
        const actualDate = new Date(String(row.actual_return_date || ""));
        if (considersActualReturnDate && !Number.isNaN(actualDate.getTime()) && actualDate > expectedDate) return true;
        const now = new Date();
        const todayUtcMidnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
        return hasOutstandingBalance(row) && expectedDate.getTime() < todayUtcMidnight;
      },
    };
    changed = true;
  });
  if (changed && typeof gridApi.setGridOption === "function") {
    gridApi.setGridOption("columnDefs", columnDefs);
  }
}
